import { type FC, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const POLLING_FREQUENCIES = [1, 10, 20, 50, 100];

type CurrentValueProps = {
  value: number;
  unit: string;
  deviceName: string;
  deviceMode?: "usb" | "bluetooth";
  isRecording: boolean;
  pollingFrequency: number;
  onFrequencyChange: (frequency: number) => void;
  onStart: () => void;
  onFinish: () => void;
  onExport?: () => void;
  onReset?: () => void;
  onResume?: () => void;
  hasData?: boolean;
  onDisconnect?: () => void;
  stats?: {
    min: number | null;
    max: number | null;
    avg: number | null;
  };
};

export const DeviceCurrentValue: FC<CurrentValueProps> = ({
  value,
  unit,
  deviceName,
  isRecording,
  pollingFrequency,
  onFrequencyChange,
  onStart,
  onFinish,
  onExport,
  onReset,
  stats,
  hasData,
  deviceMode,
  onDisconnect,
}) => {
  const { t } = useTranslation();
  const handleFrequencyChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      onFrequencyChange(Number(event.target.value));
    },
    [onFrequencyChange]
  );

  const options = useMemo(
    () =>
      POLLING_FREQUENCIES.map((freq) => (
        <option key={freq} value={freq}>
          {freq} {t('units.hz')}
        </option>
      )),
    [t]
  );

  return (
    <motion.div
      className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl shadow-[0_0_30px_-10px_rgba(0,0,0,0.6)] border border-gray-700 flex flex-col justify-between w-full"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-cyan-300 drop-shadow-sm">
            {deviceName}
          </h2>

          {onDisconnect && deviceMode !== "usb" && (
            <button
              type="button"
              onClick={onDisconnect}
              className="inline-flex items-center justify-center text-rose-400 hover:text-rose-300 cursor-pointer transition bg-transparent border-0 p-0"
              title={t('device.current.disconnect', 'Disconnect')}
            >
              <span className="sr-only">
                {t('device.current.disconnect', 'Disconnect')}
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-link-off"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 15l3 -3m2 -2l1 -1" /><path d="M11 6l.463 -.536a5 5 0 0 1 7.071 7.072l-.534 .464" /><path d="M3 3l18 18" /><path d="M13 18l-.397 .534a5.068 5.068 0 0 1 -7.127 0a4.972 4.972 0 0 1 0 -7.071l.524 -.463" /></svg>
            </button>
          )}
        </div>

        <div className="flex flex-col text-sm text-gray-400">
          <label htmlFor="polling-frequency" className="mb-1">
            {t('device.current.polling_frequency')}
          </label>
          <div className="relative">
            <select
              id="polling-frequency"
              value={pollingFrequency}
              onChange={handleFrequencyChange}
              disabled={isRecording}
              className={`block appearance-none w-full bg-gray-700/70 border border-gray-600 text-white text-center py-2 pl-3 pr-8 rounded-lg leading-tight text-base focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all duration-150 ease-in-out ${isRecording ? "opacity-60 cursor-not-allowed" : ""
                }`}
            >
              {options}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
              <svg
                className="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>
        </div>
      </div>


      {/* Current Value */}
      <div className="text-center mb-6">
        <p className="text-gray-400 text-sm tracking-wide mb-1">
          {t('device.current.current_value')}
        </p>
        <div className="inline-flex items-end justify-center">
          <span className="text-6xl font-extrabold text-white drop-shadow-lg">
            {value.toFixed(2)}
          </span>
          <span className="ml-2 text-2xl text-cyan-400 font-semibold">
            {unit}
          </span>
        </div>
      </div>

      {/* Micro Dashboard */}
      <motion.div
        className="grid grid-cols-3 gap-3 text-center text-sm text-gray-400 mb-8"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="p-3 bg-gray-800/60 rounded-xl border border-gray-700/50 hover:border-cyan-500/40 transition-colors">
          <div className="text-cyan-400 font-semibold text-lg">
            {stats?.min?.toFixed(2) ?? "-"}
          </div>
          <div className="text-gray-400 text-xs">{t('device.current.min')}</div>
        </div>

        <div className="p-3 bg-gray-800/60 rounded-xl border border-gray-700/50 hover:border-cyan-500/40 transition-colors">
          <div className="text-cyan-400 font-semibold text-lg">
            {stats?.max?.toFixed(2) ?? "-"}
          </div>
          <div className="text-gray-400 text-xs">{t('device.current.max')}</div>
        </div>

        <div className="p-3 bg-gray-800/60 rounded-xl border border-gray-700/50 hover:border-cyan-500/40 transition-colors">
          <div className="text-cyan-400 font-semibold text-lg">
            {stats?.avg?.toFixed(2) ?? "-"}
          </div>
          <div className="text-gray-400 text-xs">{t('device.current.avg')}</div>
        </div>
      </motion.div>

      {/* Controls */}
      <div className="flex justify-center mt-auto">
        {!isRecording ? (
          <motion.div
            className="flex flex-wrap justify-center gap-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {!hasData ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={onStart}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-cyan-500 text-white font-semibold shadow-lg hover:shadow-cyan-500/30 transition-all duration-200"
              >
                {t('device.current.start')}
              </motion.button>
            ) : (
              <>
                {onExport && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={onExport}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-semibold shadow-lg hover:shadow-emerald-500/30 transition-all duration-200"
                  >
                    {t('device.current.download')}
                  </motion.button>
                )}

                {onReset && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={onReset}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-rose-700 to-rose-600 text-white font-semibold shadow-lg hover:shadow-rose-500/30 transition-all duration-200"
                  >
                    {t('device.current.reset')}
                  </motion.button>
                )}
              </>
            )}

          </motion.div>
        ) : (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={onFinish}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-rose-600 to-rose-500 text-white font-semibold shadow-lg hover:shadow-rose-500/30 transition-all duration-200"
          >
            {t('device.current.stop')}
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

// Experiment selection moved to DeviceAssistantPanel
